import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  FileTextOutlined,
  // UID-409 — the BOM detail header + CTA buttons use the prototype's own
  // stroke-outline SVG glyphs (Feather/Lucide style), not `@ant-design/icons`'
  // filled/solid `LeftOutlined`/`FolderOutlined`/`ShoppingCartOutlined`/
  // `ShareAltOutlined`/`DeleteOutlined`/`PlusOutlined`/`UploadOutlined`, which
  // don't match the design's icon family. See the verbatim-prototype-path
  // components in `components/icons.tsx`.
  IconCartGlyph,
  IconChevronLeft,
  IconFolder,
  IconPlus,
  IconShare,
  IconTrash,
  IconUpload,
} from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { Tabs } from '../../components/ui/Tabs'
import { useToast } from '../../components/ui/ToastProvider'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import { useRfqBundle } from '../../lib/rfq/rfq-bundle-context'
import { toSafeImageRef } from '../../lib/thumbnail-image-ref'
import { AddPartsModal } from './components/AddPartsModal'
import {
  AssemblyTree,
  collectAllLeafIds,
  collectVisibleLeafIdsForRange,
} from './components/AssemblyTree'
import { BomOrderHistoryCard } from './components/BomOrderHistoryCard'
import { BomShareModal } from './components/BomShareModal'
import { BomStatusChip } from './components/BomStatusChip'
import { BulkActionBar } from './components/BulkActionBar'
import { formatDate, formatPrice } from './format'
import type { BomLibraryStatus, BomLineItem, BomShareResult } from './types'
import { defaultBomDetailService } from './useBomDetail'
import type { BomDetailService } from './useBomDetail'
import { useBomDetail } from './useBomDetail'

export interface BomDetailPageProps {
  /** Injectable for tests / future BFF-backed swap. */
  readonly service?: BomDetailService
}

const SKELETON_KEYS = ['a', 'b', 'c']

function countQuoteRequired(lines: readonly BomLineItem[]): number {
  return lines.reduce((total, line) => {
    const own = line.sourceable?.pricingProjection?.mode === 'quote_required' ? 1 : 0
    return total + own + (line.children ? countQuoteRequired(line.children) : 0)
  }, 0)
}

/** UID-112: leaf `part` lines whose `sourceable.pricingProjection.mode` is
 * `quote_required` — the set the "Bundle into RFQ" banner (quoteRequiredCount)
 * routes into a draft, wherever they sit in the assembly tree. */
function collectQuoteRequiredLines(lines: readonly BomLineItem[]): readonly BomLineItem[] {
  return lines.flatMap((line) => {
    const own =
      line.kind === 'part' && line.sourceable?.pricingProjection?.mode === 'quote_required'
        ? [line]
        : []
    return line.children ? [...own, ...collectQuoteRequiredLines(line.children)] : own
  })
}

/** UID-112: leaf `part` lines matching a set of selected line ids — the
 * multi-select bar's "Add to RFQ bundle" action operates over these
 * (`selectedIds` only ever contains leaf `part` ids; see AssemblyTree's
 * `leafIds` toggle). */
function collectLinesByIds(
  lines: readonly BomLineItem[],
  ids: ReadonlySet<string>,
): readonly BomLineItem[] {
  return lines.flatMap((line) => {
    const own = line.kind === 'part' && ids.has(line.lineId) ? [line] : []
    return line.children ? [...own, ...collectLinesByIds(line.children, ids)] : own
  })
}

/**
 * BOM detail (UID-47, screens.md §7) — header + status transition, stat
 * cards, RFQ-bundle banner, an expandable/reorderable assembly tree, and
 * Parts / Order history tabs. Data layer: `useBomDetail()`/
 * `BomDetailService`, mirroring the software-engineer's `GET
 * /api/boms/:bomId` handoff.
 */
export function BomDetailPage({ service = defaultBomDetailService }: BomDetailPageProps = {}) {
  const { bomId = '' } = useParams<{ bomId: string }>()
  const { state, refetch } = useBomDetail(bomId, service)
  const navigate = useNavigate()
  const { showToast } = useToast()
  const rfqBundle = useRfqBundle()

  const [tab, setTab] = useState<'parts' | 'history'>('parts')
  const rangeSelection = useRangeSelection()
  const { selectedIds } = rangeSelection
  // UID-610 — assemblies the user has collapsed. Lifted out of AssemblyTree
  // (previously a local per-row `useState`) so a shift-click range can be
  // computed over the tree's currently VISIBLE leaves only — see
  // `toggleSelectLeaf` below and `collectVisibleLeafIdsForRange`'s doc
  // comment.
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(new Set())
  const [statusBusy, setStatusBusy] = useState(false)
  const [addPartsOpen, setAddPartsOpen] = useState(false)
  // Per-action busy flags (code-review UID-47 L1) — kept separate so the
  // "Add to RFQ bundle" button's spinner never fires for an unrelated bulk
  // action (bulk remove/add-to-cart), and vice versa.
  const [removeBusy, setRemoveBusy] = useState(false)
  const [cartBusy, setCartBusy] = useState(false)
  const [addAllBusy, setAddAllBusy] = useState(false)
  const [deleteBomOpen, setDeleteBomOpen] = useState(false)
  const [deleteBomError, setDeleteBomError] = useState<string | undefined>(undefined)
  const [shareBusy, setShareBusy] = useState(false)
  const [shareResult, setShareResult] = useState<BomShareResult | null>(null)

  if (state.status === 'loading') {
    return <BomDetailSkeleton />
  }

  if (state.status === 'not_found') {
    return (
      <EmptyState
        title="BOM not found"
        detail="This BOM may have been removed, or you may not have access to it."
        action={<Button onClick={() => navigate('/parts')}>Back to library</Button>}
      />
    )
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title="We couldn’t load this BOM"
        detail={state.message}
        action={<Button onClick={refetch}>Try again</Button>}
      />
    )
  }

  const { bom, orderHistory } = state
  const quoteRequiredCount = countQuoteRequired(bom.lines)
  const allLeafIds = collectAllLeafIds(bom.lines)

  /** The header "select all" checkbox and an assembly/group row's own
   * checkbox both bulk-toggle a whole set of leaves at once — never a
   * shift-click range gesture (see `AssemblyTreeProps.onToggleSelect`'s doc
   * comment for why). */
  function toggleSelectAll(lineIds: readonly string[], select: boolean) {
    rangeSelection.toggleAll(lineIds, select)
  }

  /** UID-610 — a LEAF row's own checkbox. Ranges over the tree's currently
   * VISIBLE leaves (excludes anything hidden inside a collapsed assembly),
   * matching the rendered order the user actually sees between the anchor
   * and this row. */
  function toggleSelectLeaf(lineId: string, shiftKey: boolean) {
    rangeSelection.toggle(lineId, collectVisibleLeafIdsForRange(bom.lines, collapsedIds), shiftKey)
  }

  function toggleExpand(lineId: string) {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(lineId)) next.delete(lineId)
      else next.add(lineId)
      return next
    })
  }

  async function handleStatusChange(nextStatus: BomLibraryStatus) {
    setStatusBusy(true)
    try {
      await service.updateStatus(bom.id, nextStatus)
      refetch()
    } finally {
      setStatusBusy(false)
    }
  }

  async function handleDelete(lineId: string) {
    await service.deleteLine(bom.id, lineId)
    refetch()
  }

  async function handleMove(parentLineId: string | null, lineId: string, direction: 'up' | 'down') {
    const siblings = findSiblings(bom.lines, parentLineId)
    const index = siblings.findIndex((id) => id === lineId)
    const swapWith = direction === 'up' ? index - 1 : index + 1
    if (index < 0 || swapWith < 0 || swapWith >= siblings.length) return
    const reordered = [...siblings]
    const tmp = reordered[index] as string
    reordered[index] = reordered[swapWith] as string
    reordered[swapWith] = tmp
    await service.reorderSiblings(bom.id, parentLineId, reordered)
    refetch()
  }

  // UID-295/296 — pointer drag-and-drop reorder (measured spec §B4's drag
  // handle), IN ADDITION to (never a replacement for) `handleMove`'s
  // keyboard-operable Move up/down buttons above. Moves `draggedId` to just
  // before `targetId` within the same sibling group and persists through the
  // SAME `reorderSiblings` call `handleMove` uses — one reorder code path,
  // two input mechanisms.
  async function handleReorderDrop(
    parentLineId: string | null,
    draggedId: string,
    targetId: string,
  ) {
    const siblings = findSiblings(bom.lines, parentLineId)
    const withoutDragged = siblings.filter((id) => id !== draggedId)
    const targetIndex = withoutDragged.findIndex((id) => id === targetId)
    if (targetIndex < 0) return
    const reordered = [
      ...withoutDragged.slice(0, targetIndex),
      draggedId,
      ...withoutDragged.slice(targetIndex),
    ]
    await service.reorderSiblings(bom.id, parentLineId, reordered)
    refetch()
  }

  // UID-296 follow-up — the editable Qty cell's persistence path.
  // `service.updateLineQuantity` (software-engineer commit 5132a158) returns
  // the UPDATED `BomDetail`, but this still calls `refetch()` rather than
  // consuming that return value directly — same "one round-trip GET is the
  // single source of truth" pattern every other mutation on this page
  // already follows (handleMove/handleReorderDrop/handleDelete etc.), so the
  // tree, subtotals, and footer Total all refresh consistently.
  async function handleQuantityChange(lineId: string, quantity: number) {
    if (!service.updateLineQuantity) return
    try {
      await service.updateLineQuantity(bom.id, lineId, quantity)
      refetch()
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : 'We couldn’t update this line’s quantity. Please try again.',
        'error',
      )
    }
  }

  // UID-295/296 — per-line "Request quote" (measured spec §B4; mi8 code-
  // review fix collapsed the old link+button pair into this one button):
  // queues just this one line into the RFQ bundle and routes into
  // `/rfq/new`, reusing the SAME queue+navigate mechanism as the header's
  // "Bundle into RFQ" banner and the bulk bar's "Add to RFQ bundle" —
  // synchronous, so no busy state.
  function handleRequestQuoteForLine(lineId: string) {
    const [line] = collectLinesByIds(bom.lines, new Set([lineId]))
    if (!line) return
    queueLinesForRfq([line])
    navigate('/rfq/new')
  }

  // UID-295/296 — header "Add all to cart". `addAllToCart` IS on
  // `BomDetailService` (optional, but the real `defaultBomDetailService`
  // always provides it) — guarded here only for test doubles built before
  // this method existed.
  async function handleAddAllToCart() {
    if (addAllBusy || !service.addAllToCart) return
    setAddAllBusy(true)
    try {
      await service.addAllToCart(bom.id)
      showToast('Added this BOM’s parts to your cart.', 'success')
    } catch {
      showToast('Could not add this BOM to your cart. Please try again.', 'error')
    } finally {
      setAddAllBusy(false)
    }
  }

  // UID-296 follow-up — header "Share". `service.shareBom` (5132a158) mints a
  // one-time share token; it's surfaced through `BomShareModal` (the ONLY
  // place the user can see/copy it — the server persists only its hash).
  async function handleShare() {
    if (shareBusy || !service.shareBom) return
    setShareBusy(true)
    try {
      const result = await service.shareBom(bom.id)
      setShareResult(result)
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : 'We couldn’t create a share link for this BOM. Please try again.',
        'error',
      )
    } finally {
      setShareBusy(false)
    }
  }

  // UID-296 follow-up — header "Delete" (the WHOLE bom, not a line), backed
  // by `service.deleteBom` (5132a158). Mirrors `WorkspacePage`'s established
  // "keep the ConfirmDialog open and show its own `error` prop on failure"
  // pattern (rather than a toast) — a destructive confirm dialog's own error
  // state is the more discoverable place for a rejection than a toast that
  // can be missed while the dialog is still covering the screen.
  async function handleConfirmDeleteBom() {
    if (!service.deleteBom) return
    setDeleteBomError(undefined)
    try {
      await service.deleteBom(bom.id)
      setDeleteBomOpen(false)
      navigate('/parts')
    } catch (error) {
      setDeleteBomError(
        error instanceof Error ? error.message : 'We couldn’t delete this BOM. Try again.',
      )
    }
  }

  async function handleBulkRemove() {
    setRemoveBusy(true)
    try {
      for (const lineId of selectedIds) {
        await service.deleteLine(bom.id, lineId)
      }
      rangeSelection.clear()
      refetch()
    } finally {
      setRemoveBusy(false)
    }
  }

  // UID-112: the multi-select bar's "Add to RFQ bundle" action is a CREATION
  // PATH into `/rfq/new`, not a dead end back to the bare list — queue the
  // selected leaf parts into the cross-screen bundle, then route straight
  // into the compose screen with them pre-populated. Synchronous (no network
  // call of its own), so — like the pre-UID-112 `navigate('/rfq')` it
  // replaces — it never needs a busy state.
  function handleAddSelectedToRfq() {
    const lines = collectLinesByIds(bom.lines, selectedIds)
    queueLinesForRfq(lines)
    rangeSelection.clear()
    navigate('/rfq/new')
  }

  // UID-112: the header's "Bundle into RFQ" banner previously routed to the
  // bare RFQ list with nothing carried forward. It now queues every part in
  // the tree that actually needs a manual quote (regardless of selection)
  // and routes into the compose screen with them pre-populated.
  function handleBundleQuoteRequiredIntoRfq() {
    const lines = collectQuoteRequiredLines(bom.lines)
    queueLinesForRfq(lines)
    navigate('/rfq/new')
  }

  // UID-335 follow-up — `thumbnailUrl` is a SIBLING field of `sourceable`
  // (never on `sourceable.imageRef` itself). See `toSafeImageRef`'s doc
  // comment for why the merge needs the root-relative-vs-absolute
  // distinction it applies.
  // UID-552 (ADR-0170) — `beginDraft`, not `addItems`: every caller of this
  // helper (`handleRequestQuoteForLine`, `handleAddSelectedToRfq`,
  // `handleBundleQuoteRequiredIntoRfq`) is a "Create RFQ"/"Add to RFQ" entry
  // point and must start a genuinely NEW draft, never merge into whatever
  // slot happened to be active.
  function queueLinesForRfq(lines: readonly BomLineItem[]) {
    rfqBundle.beginDraft(
      lines
        .filter((line) => line.sourceable !== undefined)
        .map((line) => {
          const sourceable = line.sourceable as NonNullable<BomLineItem['sourceable']>
          const imageRef = toSafeImageRef(line.thumbnailUrl, sourceable.id)
          return {
            sourceable: imageRef !== undefined ? { ...sourceable, imageRef } : sourceable,
            name: line.name,
            sku: sourceable.catalogSku ?? null,
          }
        }),
    )
  }

  // The per-row "Add to cart" button (AssemblyTree) and the multi-select
  // bar's bulk "Add to cart" action share this one handler/service call —
  // one code path for both, per UID-47's "reuse the mechanism, don't invent
  // a new one" requirement. `addLineToCart` is backed by the real BOM-line →
  // cart endpoint (UID-107, see useBomDetail.ts), so a success surfaces the
  // "Added to cart." toast and a rejection the neutral error toast.
  async function handleAddToCart(lineId: string) {
    // In-flight guard + error surfacing: a rejection must surface as a toast,
    // not an unhandled promise rejection, and a second concurrent add must be
    // blocked.
    if (cartBusy) return
    setCartBusy(true)
    try {
      await service.addLineToCart(bom.id, lineId)
      showToast('Added to cart.', 'success')
    } catch {
      showToast('Could not add to cart. Please try again.', 'error')
    } finally {
      setCartBusy(false)
    }
  }

  async function handleBulkAddToCart() {
    if (cartBusy) return
    setCartBusy(true)
    try {
      // UID-288: attempt every selected line INDEPENDENTLY. The old loop
      // `await`ed inside one try/catch, so the first rejection aborted the
      // batch — lines already added stayed committed server-side (the cart
      // endpoint is add-or-increment, not transactional), the rest were never
      // tried, and a single blanket toast made a partial success look like a
      // total failure. We attempt each line and never short-circuit, so one
      // bad line no longer skips the others.
      //
      // The adds run SEQUENTIALLY, not concurrently. Every per-line POST is a
      // read-modify-write on the SAME single cart aggregate, guarded server-side
      // by optimistic concurrency (compare-and-swap) with a bounded retry
      // (cart-service.ts `MAX_MUTATION_ATTEMPTS`). Firing them in parallel
      // (the earlier `Promise.allSettled(map(...))`) made N adds contend on one
      // cart: only one wins per CAS round, so a selection larger than the retry
      // budget left the losers exhausting their retries and failing with
      // `cart/concurrent_modification` (409) — a self-inflicted thundering herd.
      // Awaiting each add before starting the next serializes the writes so
      // every one lands on fresh state with zero contention.
      const lineIds = [...selectedIds]
      const failedIds: string[] = []
      for (const lineId of lineIds) {
        try {
          await service.addLineToCart(bom.id, lineId)
        } catch {
          failedIds.push(lineId)
        }
      }
      const total = lineIds.length
      const succeeded = total - failedIds.length

      if (failedIds.length === 0) {
        // Full success — leave the selection as-is (unchanged behavior; the
        // user may follow up with another bulk action on the same rows).
        showToast(`${total} part${total === 1 ? '' : 's'} added to cart.`, 'success')
      } else if (succeeded === 0) {
        showToast('Could not add parts to cart. Please try again.', 'error')
        // Selection unchanged — every line failed, so a retry re-attempts all.
      } else {
        // Partial: reveal the split rather than a blanket 0-of-N message, and
        // narrow the selection to ONLY the failed lines so a retry re-attempts
        // just those — never re-adding (bumping the quantity of) the ones that
        // already landed server-side.
        showToast(
          `Added ${succeeded} of ${total} parts to cart. Couldn’t add the rest — try again.`,
          'error',
        )
        rangeSelection.setSelectedIds(new Set(failedIds))
      }
    } finally {
      setCartBusy(false)
    }
  }

  return (
    <section aria-labelledby="bom-detail-heading" className="bom-detail-page">
      {/* AC1 (QA fix round) — a persistent "Back to library" link at the top
          of the LOADED detail page, not just the not-found empty state
          below. Same destination as that empty state's own action. */}
      <button type="button" className="bom-detail-back-link" onClick={() => navigate('/parts')}>
        <IconChevronLeft />
        Back to library
      </button>
      <div className="card bom-detail-header">
        {/* UID-379 (V1) — a violet-tinted square folder-icon chip to the left
            of the title, matching the tinted-icon-chip pattern the BOM card
            grid already uses (`.bom-card-icon-tile`, BomCardGrid.tsx).
            UID-409 — 52×52 tile / 26px glyph and the prototype's own stroke-
            outline folder (`IconFolder`), matching the design header. */}
        <span className="bom-detail-header-icon-tile" aria-hidden="true">
          <IconFolder />
        </span>
        <div className="bom-detail-header-body">
          <h1 id="bom-detail-heading" className="display-heading">
            {bom.name}
          </h1>
          {/* UID-379 (V2) — one-line BOM description, above the status/date
              row. `BomDetail.description` is the SAME optional field
              `BomCardGrid` already reads for the library preview
              (UID-295/296) — rendered only when present, never fabricated. */}
          {bom.description && <p className="bom-detail-header-description">{bom.description}</p>}
          <div className="bom-detail-header-meta">
            {/* UID-379 (V3) — the status chip moves from the top-right action
                cluster into the header body, grouped with the title/
                description alongside "Updated <date>", left-aligned. Still
                interactive (dropdown chevron for inline status change) and
                still shows its icon — `hideIcon` is left at its default
                `false` (only `BomCardGrid`'s read-only card chip hides it). */}
            <BomStatusChip
              status={bom.status}
              onChange={handleStatusChange}
              disabled={statusBusy}
              className="bom-status-pill"
            />
            <p className="caption-text">Updated {formatDate(bom.updatedAt)}</p>
          </div>
        </div>
        <div className="bom-detail-header-actions">
          {/* UID-379 (V4) — filled primary (navy) button with a leading cart
              icon, replacing the old iconless `secondary` button.
              UID-409 — 40px (`lg`) to match the design's header actions, and
              the prototype's stroke-outline cart (`IconCartGlyph`, the same
              glyph the "Add priced to cart" CTA uses) instead of Ant's filled
              `ShoppingCartOutlined`. */}
          <Button variant="primary" size="lg" onClick={handleAddAllToCart} loading={addAllBusy}>
            <IconCartGlyph />
            Add all to cart
          </Button>
          {/* UID-379 (V5) — leading share glyph. UID-409 — 40px (`lg`) and the
              prototype's stroke-outline share-2 glyph (`IconShare`) instead of
              Ant's filled `ShareAltOutlined`. */}
          <Button variant="outline" size="lg" onClick={handleShare} loading={shareBusy}>
            <IconShare />
            Share
          </Button>
          {/* UID-379 (V6) — leading trash glyph on the red danger-outline
              Delete button. UID-409 — 40px (`lg`) and the prototype's stroke-
              outline trash-2 glyph (`IconTrash`) instead of Ant's filled
              `DeleteOutlined`. */}
          <Button
            variant="danger-outline"
            size="lg"
            className="bom-delete-btn"
            onClick={() => setDeleteBomOpen(true)}
          >
            <IconTrash />
            Delete
          </Button>
        </div>
      </div>

      <div className="bom-detail-stats">
        {/* NOT `interactive` (code-review UID-47 W1) — these cards aren't
            click targets, so they don't get the hover-lift affordance that
            implies clickability. */}
        <Card>
          <p className="caption-text">Total parts</p>
          <p className="bom-detail-stat-value">{bom.stats.totalParts}</p>
          {bom.stats.distinctPartCount !== undefined && (
            <p className="bom-detail-stat-caption caption-text">
              {bom.stats.distinctPartCount} unique
            </p>
          )}
        </Card>
        <Card>
          <p className="caption-text">Estimated cost</p>
          <p className="bom-detail-stat-value">
            {bom.stats.estimatedCost ? formatPrice(bom.stats.estimatedCost) : '—'}
          </p>
          <p className="bom-detail-stat-caption caption-text">Before shipping</p>
        </Card>
        <Card>
          <p className="caption-text">Max lead time</p>
          <p className="bom-detail-stat-value">
            {bom.stats.maxLeadTimeDays !== null ? `${bom.stats.maxLeadTimeDays} days` : '—'}
          </p>
          <p className="bom-detail-stat-caption caption-text">Longest item</p>
        </Card>
      </div>

      {quoteRequiredCount > 0 && (
        <div className="bom-rfq-banner" role="note">
          <span>
            {quoteRequiredCount} part{quoteRequiredCount === 1 ? '' : 's'} in this BOM require
            {quoteRequiredCount === 1 ? 's' : ''} a quote.
          </span>
          <Button variant="purple" size="sm" onClick={handleBundleQuoteRequiredIntoRfq}>
            Bundle into RFQ
          </Button>
        </div>
      )}

      {/* UID-379 (V7) — the design shows no heavy bordered pill on "Parts";
          only "Order history" carries a subtle count. Drop the `badge` from
          the "Parts" tab item entirely rather than softening the shared
          `.tab-count` styling globally (that's the ADR-0065 a11y-
          perceivability shape every other Tabs consumer still relies on). */}
      {/* UID-381 — "Add parts"/"Upload" used to render INSIDE the Parts-tab
          branch below, so they vanished on Order history. Lifted to this
          shared row (wrapping both the toolbar and `<Tabs>` in
          `.bom-detail-tabs-row`, CSS-positioned top-right to match the
          design) so both tabs keep the same top-right actions; handlers are
          unchanged (both still open the same `setAddPartsOpen` add-parts
          flow — see the UID-379 (V8) comment on the buttons themselves). */}
      <div className="bom-detail-tabs-row">
        <div className="bom-detail-parts-toolbar">
          {/* UID-409 — 32px (`md`) to match the design's toolbar buttons, the
              prototype's stroke-outline plus glyph (`IconPlus`) instead of
              Ant's filled `PlusOutlined`, and the design's soft brand-tinted
              fill (`.bom-add-parts-btn`) instead of the `purple` accent — the
              design's teal-50/teal/teal-100 maps to the Misumi brand-tint
              tokens (see tokens.css). */}
          <Button
            variant="outline"
            size="md"
            className="bom-add-parts-btn"
            onClick={() => setAddPartsOpen(true)}
          >
            <IconPlus size={15} />
            Add parts
          </Button>
          {/* UID-409 — 32px (`md`) and the prototype's stroke-outline upload-
              tray glyph (`IconUpload`) instead of Ant's filled `UploadOutlined`. */}
          <Button variant="outline" size="md" onClick={() => setAddPartsOpen(true)}>
            <IconUpload size={15} />
            Upload
          </Button>
        </div>
        <Tabs
          items={[
            { id: 'parts', label: 'Parts' },
            {
              id: 'history',
              label: 'Order history',
              badge: { kind: 'count', count: orderHistory.length },
            },
          ]}
          activeId={tab}
          onChange={(id) => setTab(id as 'parts' | 'history')}
          aria-label="BOM detail sections"
          className="bom-detail-tabs"
        >
          {(activeId) => {
            if (activeId === 'history') {
              if (orderHistory.length === 0) {
                return (
                  <EmptyState
                    icon={<FileTextOutlined aria-hidden="true" />}
                    title="No order history yet"
                    detail="Once this BOM is ordered, every order placed against it will show up here."
                  />
                )
              }
              return (
                <div className="bom-order-history-list">
                  {orderHistory.map((order) => (
                    <BomOrderHistoryCard key={order.orderId} order={order} />
                  ))}
                </div>
              )
            }

            return (
              <div
                className={
                  selectedIds.size > 0
                    ? 'bom-detail-parts-tab bom-detail-parts-tab--bar-visible'
                    : 'bom-detail-parts-tab'
                }
              >
                {bom.lines.length === 0 ? (
                  <EmptyState
                    title="No parts in this BOM yet"
                    action={<Button onClick={() => setAddPartsOpen(true)}>Add parts</Button>}
                  />
                ) : (
                  <div className="bom-parts-table-card">
                    {/* MA3 (code-review fix, QA fix round) — these are VISUAL
                      header cells over the `role="tree"` below, not a real
                      `<table>`/`role="treegrid"`, so they aren't
                      programmatically associated with the tree's data cells
                      (WCAG 1.3.1). Minimum fix applied: each data cell in
                      AssemblyTree.tsx carries its own self-describing
                      `aria-label` (see that file's comment). A full
                      `role="treegrid"` restructure is a bigger change,
                      left as a follow-up. */}
                    <div className="bom-parts-table-header">
                      <span className="bom-parts-table-header-cell bom-parts-table-header-cell--name">
                        <input
                          type="checkbox"
                          aria-label="Select all parts"
                          checked={
                            allLeafIds.length > 0 && allLeafIds.every((id) => selectedIds.has(id))
                          }
                          onChange={(event) => toggleSelectAll(allLeafIds, event.target.checked)}
                        />
                        Name &amp; drawing
                      </span>
                      <span className="bom-parts-table-header-cell">Requirements</span>
                      <span className="bom-parts-table-header-cell">Source</span>
                      <span className="bom-parts-table-header-cell">Qty</span>
                      <span className="bom-parts-table-header-cell">Unit price</span>
                      <span className="bom-parts-table-header-cell">Subtotal</span>
                    </div>
                    <AssemblyTree
                      lines={bom.lines}
                      selectedIds={selectedIds}
                      onToggleSelect={toggleSelectAll}
                      onToggleSelectLeaf={toggleSelectLeaf}
                      collapsedIds={collapsedIds}
                      onToggleExpand={toggleExpand}
                      onDelete={handleDelete}
                      onMove={handleMove}
                      onAddToCart={handleAddToCart}
                      onRequestQuote={handleRequestQuoteForLine}
                      onQuantityChange={handleQuantityChange}
                      onReorderDrop={handleReorderDrop}
                    />
                    <div className="bom-parts-table-footer">
                      <span>Total</span>
                      <span className="bom-parts-table-footer-total">
                        {bom.stats.estimatedCost ? formatPrice(bom.stats.estimatedCost) : '—'}
                      </span>
                    </div>
                  </div>
                )}
                {/* rfqBusy is intentionally not passed: "Add to RFQ" navigates
                  synchronously, so it never has an in-flight state here. Only
                  the async cart/remove actions report busy. */}
                <BulkActionBar
                  selectedCount={selectedIds.size}
                  onAddToRfq={handleAddSelectedToRfq}
                  onAddToCart={handleBulkAddToCart}
                  onRemove={handleBulkRemove}
                  onClear={() => rangeSelection.clear()}
                  cartBusy={cartBusy}
                  removeBusy={removeBusy}
                />
              </div>
            )
          }}
        </Tabs>
      </div>

      <AddPartsModal
        open={addPartsOpen}
        onClose={() => setAddPartsOpen(false)}
        search={(query) => service.searchAddParts(query)}
        onAdd={async (resultIds) => {
          await service.addLines(bom.id, resultIds)
          refetch()
        }}
      />

      {/* UID-295/296 — a destructive whole-BOM action always gets an
          explicit confirmation (accessibility-wcag). A rejection surfaces
          INSIDE the still-open dialog via its own `error` prop (same
          established pattern as WorkspacePage's delete confirm) rather than
          a toast the dialog itself would visually cover. */}
      <ConfirmDialog
        open={deleteBomOpen}
        title="Delete this BOM?"
        description={`"${bom.name}" and all of its lines will be permanently removed. This can’t be undone.`}
        confirmLabel="Delete BOM"
        onCancel={() => {
          setDeleteBomOpen(false)
          setDeleteBomError(undefined)
        }}
        onConfirm={handleConfirmDeleteBom}
        error={deleteBomOpen ? deleteBomError : undefined}
      />

      {/* UID-296 — the one-time share-token reveal (see BomShareModal's doc
          comment). Closing clears the token from memory rather than leaving
          it sitting in state after the user is done with it. */}
      <BomShareModal
        open={shareResult !== null}
        onClose={() => setShareResult(null)}
        result={shareResult}
      />
    </section>
  )
}

function findSiblings(
  lines: readonly BomLineItem[],
  parentLineId: string | null,
): readonly string[] {
  if (parentLineId === null) return lines.map((l) => l.lineId)
  for (const line of lines) {
    if (line.lineId === parentLineId) return (line.children ?? []).map((c) => c.lineId)
    if (line.children) {
      const found = findSiblings(line.children, parentLineId)
      if (found.length > 0) return found
    }
  }
  return []
}

function BomDetailSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading BOM detail" className="bom-detail-skeleton">
      {SKELETON_KEYS.map((key) => (
        <Skeleton key={key} height={48} />
      ))}
    </div>
  )
}
